import React, { useMemo, useState } from 'react';
import {
  ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar
} from 'recharts';
import { ShoppingCart } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import { useBulleAuSurvol } from './BulleSurvol';
import { agregerParGranularite } from '../utils/granulariteSeries';
import { COULEUR_SERIE_VALEUR } from './RepartitionChart';

/**
 * Le chiffre d'affaires d'une boutique PrestaShop : ses cartes, sa courbe, sa décomposition.
 *
 * ## Pourquoi un composant plutôt que `PerformanceChart`
 *
 * `PerformanceChart.jsx` est générique de structure mais **publicitaire de vocabulaire** :
 * `clics`, `impressions`, `cpc`, `cos`, `roas`. Y plier un chiffre d'affaires de boutique
 * demanderait plus de contorsions que d'écrire ce qui suit — et laisserait un écran dont les
 * libellés parlent d'une régie.
 *
 * ## ⚠️ La devise est reçue, jamais supposée
 *
 * Le reste du produit code l'euro en dur : `RepartitionChart` dans ses formateurs,
 * `SaisieConsolide` qui n'a pas de devise du tout. Une boutique en livres doit dire « £ », et
 * c'est la seule chose que cet écran sait d'elle que les autres ignorent.
 *
 * ## La granularité se calcule ici, pas au serveur
 *
 * Le serveur rend toujours des journées : changer de granularité ne doit pas relire la boutique,
 * qui n'agrège rien et facture chaque lecture au client. `agregerParGranularite` déclare ce qui
 * s'additionne et ce qui se **recalcule** — ⚠️ un panier moyen hebdomadaire est le CA de la
 * semaine divisé par ses commandes, jamais la moyenne de sept paniers quotidiens.
 */

const GRANULARITES = [
  { cle: 'day', libelle: 'Jour' },
  { cle: 'week', libelle: 'Semaine' },
  { cle: 'month', libelle: 'Mois' },
  { cle: 'quarter', libelle: 'Trimestre' },
  { cle: 'year', libelle: 'Année' }
];

/** Ce qui s'additionne d'une journée à l'autre, et ce qui se recalcule sur les sommes. */
const CONTRAT = {
  sommes: [
    'totalRevenue', 'totalRevenueTtc', 'transactions', 'productsRevenue',
    'shipping', 'discounts', 'refunds', 'ordersExcluded'
  ],
  ratios: { avgOrderValue: ['totalRevenue', 'transactions'] }
};

/** Un formateur de montants qui connaît la devise de la boutique. */
export function formateurMontant(devise) {
  const symbole = { EUR: '€', GBP: '£', USD: '$', CHF: 'CHF' }[devise] || devise || '';
  return (valeur) => {
    if (valeur === null || valeur === undefined) return '—';
    const nombre = Number(valeur).toLocaleString('fr-FR', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    // Les devises à symbole se postfixent en français ; un code ISO se lit mieux détaché.
    return symbole.length <= 1 ? `${nombre} ${symbole}` : `${nombre} ${symbole}`;
  };
}

const formateurEntier = (valeur) =>
  (valeur === null || valeur === undefined ? '—' : Number(valeur).toLocaleString('fr-FR'));

function PrestashopPerformanceChart({
  daily = [],
  summary = null,
  previousSummary = null,
  devise = 'EUR',
  granularity = 'day',
  onGranularityChange,
  loading = false,
  KPICard,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'prestashop_performance'
}) {
  const [metriqueBarre, setMetriqueBarre] = useState('totalRevenue');
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  const serie = useMemo(
    () => agregerParGranularite(daily, granularity, CONTRAT),
    [daily, granularity]
  );

  const montant = useMemo(() => formateurMontant(devise), [devise]);

  /** Les valeurs d'une métrique, pour le minigraphe des cartes. */
  const valeursDe = (champ) => serie.map(p => p[champ] ?? 0);

  if (loading) return null;

  /*
   * Les sept cartes. Les quatre dernières sont la **décomposition** : elles expliquent un chiffre
   * d'affaires qui bouge sans que les commandes bougent — une remise accordée, un port offert,
   * un avoir émis.
   *
   * ⚠️ Les avoirs ne sont **pas** déduits du chiffre d'affaires : un remboursement ne réduit pas
   * le montant payé dans PrestaShop, et le brut est ce que la boutique affiche dans son propre
   * rapport de ventes. La carte les montre à côté, pas en moins.
   */
  const cartes = [
    { titre: 'Chiffre d\'affaires', champ: 'totalRevenue', couleur: COULEUR_SERIE_VALEUR, format: montant,
      aide: 'Hors taxes et hors frais de port, dans la devise de la boutique.\nSeules les commandes du périmètre retenu sont comptées.' },
    { titre: 'Commandes', champ: 'transactions', couleur: '#16a34a', format: formateurEntier,
      aide: 'Commandes du périmètre retenu — ni les paniers en attente, ni les annulées.' },
    { titre: 'Panier moyen', champ: 'avgOrderValue', couleur: '#9333ea', format: montant,
      aide: 'Chiffre d\'affaires divisé par les commandes du même périmètre.\nSur une semaine, c\'est le CA de la semaine sur ses commandes — jamais la moyenne des paniers quotidiens.' },
    { titre: 'CA produits', champ: 'productsRevenue', couleur: '#0891b2', format: montant,
      aide: 'Le chiffre d\'affaires des produits avant remise.\nL\'écart avec le chiffre d\'affaires vient des remises, et d\'un éventuel bon « frais de port offerts ».' },
    { titre: 'Frais de port', champ: 'shipping', couleur: '#ca8a04', format: montant,
      aide: 'Exclus du chiffre d\'affaires, comme le veut la définition du back-office.' },
    { titre: 'Remises', champ: 'discounts', couleur: '#dc2626', format: montant,
      aide: 'Remises accordées, déjà déduites du chiffre d\'affaires.' },
    { titre: 'Avoirs', champ: 'refunds', couleur: '#64748b', format: montant,
      aide: 'Avoirs émis sur la période, au jour de l\'avoir.\nIls ne sont pas déduits du chiffre d\'affaires : un remboursement ne réduit pas le montant payé dans PrestaShop.' }
  ];

  /*
   * La couleur de l'histogramme suit la grandeur choisie, et se lit donc sans légende.
   *
   * ⚠️ Elle est **dérivée des cartes**, jamais recopiée : une couleur de carte ajustée sans son
   * jumeau dans le graphique ne casserait rien et ne se verrait qu'en comparant les deux. Il n'y
   * a donc aucune exception à maintenir — le chiffre d'affaires porte le mauve jusque dans sa
   * carte, de sorte que la grandeur garde la même couleur partout : ici, dans « États de
   * commande » et dans « Meilleures ventes ».
   */
  const couleurParChamp = Object.fromEntries(cartes.map(c => [c.champ, c.couleur]));
  const couleurDeLaBarre = couleurParChamp[metriqueBarre] || COULEUR_SERIE_VALEUR;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', gap: '12px', flexWrap: 'wrap'
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShoppingCart size={18} /> Ventes de la boutique
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {GRANULARITES.map(g => (
            <button
              key={g.cle}
              onClick={() => onGranularityChange && onGranularityChange(g.cle)}
              style={{
                padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                border: granularity === g.cle ? '1px solid #111827' : '1px solid #d1d5db',
                background: granularity === g.cle ? '#111827' : 'white',
                color: granularity === g.cle ? 'white' : '#374151'
              }}
            >
              {g.libelle}
            </button>
          ))}
          {toggleClientModuleVisibility && (
            <ModuleVisibilityToggle
              moduleId={moduleId}
              userRole={userRole}
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
            />
          )}
        </div>
      </div>

      {/* Les cartes. `KPICard` descend d'`App.js` : une seule définition pour tous les dashboards. */}
      {KPICard && summary && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px', marginBottom: '24px'
        }}>
          {cartes.map(carte => (
            <div
              key={carte.champ}
              className={carte.aide ? 'ksi-survolable' : undefined}
              onMouseEnter={carte.aide ? survoler(carte.aide) : undefined}
              onMouseLeave={carte.aide ? quitter : undefined}
            >
              <KPICard
                title={carte.titre}
                value={summary[carte.champ] ?? 0}
                color={carte.couleur}
                currentPeriodData={valeursDe(carte.champ)}
                previousValue={previousSummary ? previousSummary[carte.champ] : null}
                formatter={carte.format}
                /* ⚠️ Une remise ou un avoir qui monte n'est pas une bonne nouvelle. */
                invertVariation={carte.champ === 'discounts' || carte.champ === 'refunds'}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {[
          { cle: 'totalRevenue', libelle: 'Chiffre d\'affaires' },
          { cle: 'productsRevenue', libelle: 'CA produits' },
          { cle: 'shipping', libelle: 'Frais de port' }
        ].map(m => (
          <button
            key={m.cle}
            onClick={() => setMetriqueBarre(m.cle)}
            style={{
              padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
              // Le bouton actif porte la couleur qu'il donne à l'histogramme : une teinte de
              // bouton sans rapport avec la barre ferait chercher à quoi elle se rapporte.
              border: metriqueBarre === m.cle
                ? `1px solid ${couleurParChamp[m.cle]}`
                : '1px solid #d1d5db',
              background: 'white',
              color: metriqueBarre === m.cle ? couleurParChamp[m.cle] : '#374151',
              fontWeight: metriqueBarre === m.cle ? 600 : 400
            }}
          >
            {m.libelle}
          </button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={serie} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="gauche" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="droite" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(valeur, nom) => (nom === 'Commandes' ? formateurEntier(valeur) : montant(valeur))}
          />
          <Legend />
          <Bar
            yAxisId="gauche"
            dataKey={metriqueBarre}
            name={{ totalRevenue: 'Chiffre d\'affaires', productsRevenue: 'CA produits', shipping: 'Frais de port' }[metriqueBarre]}
            fill={couleurDeLaBarre}
            radius={[4, 4, 0, 0]}
          />
          <Line
            yAxisId="droite"
            type="monotone"
            dataKey="transactions"
            name="Commandes"
            stroke="#16a34a"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {bulle}
    </div>
  );
}

export default PrestashopPerformanceChart;
